import { Box, Button, Dialog, IconButton } from '@mui/material';
import { ERROR_REC_GREEN, MUSEFLOW_OFF_WHITE_1 } from 'Components/Colors';
import { CustomButton } from 'Components/StyledComponents';
import CloseIconAsset from 'assets/images/CloseIcon.png';
import RoadmapTierComplete from 'assets/images/RoadmapTierComplete.png';
import type { AppNotification } from 'Types/notifications';

interface AnnouncementModalProps {
  open: boolean;
  notification: AppNotification;
  onDismiss: () => void;
  onPrimaryAction: () => void;
}

const AnnouncementModal = ({
  open,
  notification,
  onDismiss,
  onPrimaryAction,
}: AnnouncementModalProps) => {
  const hasImage = !!notification.image_url;
  const hasCta = !!notification.cta_url;
  const primaryLabel =
    notification.cta_label?.trim() || (notification.dismissible ? 'Got it' : 'Continue');

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (
          !notification.dismissible &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }
        onDismiss();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          width: 'min(96vw, 720px)',
          maxWidth: '720px',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'transparent',
          boxShadow: '0 32px 90px rgba(0, 0, 0, 0.45)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(4, 10, 19, 0.72)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '8px',
          minHeight: { xs: '390px', md: '450px' },
          background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
          border: '1px solid rgba(97, 106, 117, 0.7)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(7, 20, 35, 0.08) 0%, rgba(7, 20, 35, 0.18) 100%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(29, 47, 68, 0.06) 0%, rgba(7, 20, 35, 0.22) 100%)',
          },
        }}
      >
        <Box
          component="img"
          src={RoadmapTierComplete}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.2,
            zIndex: 0,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />

        {notification.dismissible && (
          <IconButton
            onClick={onDismiss}
            sx={{
              position: 'absolute',
              top: { xs: 12, md: 16 },
              right: { xs: 12, md: 16 },
              zIndex: 2,
              padding: 0,
              color: '#FFFFFF',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            aria-label="Close announcement"
          >
            <Box
              component="img"
              src={CloseIconAsset}
              alt=""
              aria-hidden
              sx={{
                width: { xs: '16px', md: '18px' },
                height: { xs: '16px', md: '18px' },
                opacity: 0.95,
              }}
            />
          </IconButton>
        )}

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            minHeight: { xs: '390px', md: '450px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            px: { xs: 3, md: 6 },
            pt: { xs: 6, md: 7 },
            pb: { xs: 4, md: 4.5 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '620px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flex: 1,
            }}
          >
            <Box>
              <Box
                sx={{
                  color: ERROR_REC_GREEN,
                  fontFamily: 'Lato',
                  fontSize: { xs: '28px', md: '36px' },
                  lineHeight: 1.12,
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                  maxWidth: '100%',
                }}
              >
                {notification.title}
              </Box>

              <Box
                sx={{
                  marginTop: { xs: 2.5, md: 3 },
                  color: MUSEFLOW_OFF_WHITE_1,
                  fontFamily: 'Lato',
                  fontSize: { xs: '18px', md: '20px' },
                  lineHeight: 1.5,
                  letterSpacing: '0.01em',
                  whiteSpace: 'pre-line',
                  maxWidth: '100%',
                }}
              >
                {notification.body}
              </Box>
            </Box>

            {hasImage && (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '360px',
                  minHeight: { xs: '180px', md: '220px' },
                  marginTop: { xs: 3, md: 4 },
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backgroundColor: 'rgba(7, 20, 35, 0.3)',
                }}
              >
                <Box
                  component="img"
                  src={notification.image_url || undefined}
                  alt={notification.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: '180px', md: '220px' },
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                marginTop: 'auto',
                paddingTop: { xs: 4, md: 5 },
              }}
            >
              <CustomButton onClick={onPrimaryAction} sx={{ minWidth: '140px' }}>
                {primaryLabel}
              </CustomButton>

              {hasCta && notification.dismissible && (
                <Button
                  onClick={onDismiss}
                  sx={{
                    color: MUSEFLOW_OFF_WHITE_1,
                    fontFamily: 'Lato',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    },
                  }}
                >
                  Maybe Later
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AnnouncementModal;
